import { ChevronRight } from "lucide-react";
import { Subcategory, SettingsCategory } from "../constants/settingsStructure";
import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";

interface SettingCardProps {
  subcategory: Subcategory;
  category: SettingsCategory;
  onClick: () => void;
  isSelected?: boolean;
}

const SettingCard = ({ subcategory, category, onClick, isSelected = false }: SettingCardProps) => {
  const getScopeBadge = () => {
    if (!subcategory.settingScope) return null;

    if (subcategory.settingScope === "personal") {
      return (
        <Badge
          variant="outline"
          className="text-xs bg-accent/50 text-accent-foreground border-accent-foreground/5 font-medium flex items-center gap-1.5 px-2.5 py-0.5"
        >
          <i className="fa-regular fa-user text-xs" />
          Personal
        </Badge>
      );
    } else if (subcategory.settingScope === "board") {
      return (
        <Badge
          variant="outline"
          className="text-xs bg-primary/15 text-primary border-primary/5 font-medium flex items-center gap-1.5 px-2.5 py-0.5"
        >
          <i className="fa-regular fa-grid-2 text-xs" />
          Board-wide
        </Badge>
      );
    }
    return null;
  };

  return (
    <div
      role="button"
      onClick={onClick}
      className={cn(
        "flex items-start justify-between gap-4 group relative p-4 rounded-lg border bg-card cursor-pointer transition-all duration-200 ease-out hover:border-primary/30 hover:bg-primary/5",
        isSelected
          ? "border-primary bg-primary/10 shadow-md ring-1 ring-primary/20"
          : "border-border"
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-semibold text-foreground text-base">{subcategory.label}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{subcategory.description}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            className="text-xs bg-secondary/50 text-secondary-foreground border-secondary-foreground/5 font-medium flex items-center gap-1.5 px-2.5 py-0.5"
          >
            <i className={cn(category.icon, "text-[10px]")} />
            {category.label}
          </Badge>
          {getScopeBadge()}
        </div>
      </div>
      <ChevronRight className={cn(
        "h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 mt-1",
        isSelected && "text-primary"
      )} />
    </div>
  );
};

export default SettingCard;
